import React from 'react';
import styled from 'styled-components';
import {
  spacer12,
  spacer16,
  spacer40,
  heading4FontSize,
  screenSmMax,
  white,
  Type,
  TooltipTrigger,
  Tooltip,
  cssColorValue,
} from '@spotify-internal/encore-web-v5';
import { IconWithText, IconArrowRight } from '@spotify-internal/encore-web-v6';
import type { ProviderService } from './types';
import { Link, useWidth } from '../ui';
import { ContentfulImage } from '../Contentful/ContentfulImage';
import { useT } from '@spotify-internal/i18n-core';
import { TRACK_CAT } from './constants';

const IMG_SIZE = '72px';

const TileContainer = styled.li<{ ref: any }>`
  width: 100%;
  display: flex;
  flex-flow: row wrap;
  padding: ${spacer16} 0;

  a {
    text-decoration: underline;
  }
`;

const Logo = styled.div`
  &,
  img {
    min-height: ${IMG_SIZE};
    min-width: ${IMG_SIZE};
    max-height: ${IMG_SIZE};
    max-width: ${IMG_SIZE};
    object-fit: contain;
    padding: 0;
  }
`;

const Name = styled.div`
  width: 156px;
  align-self: flex-start;
  height: ${IMG_SIZE};
  display: flex;
  flex-direction: column;
  align-content: flex-start;
  margin: 0 ${spacer12};

  div {
    margin: 0;
    a {
      text-decoration: none;
    }
    h3 {
      margin: 0;
      width: 100%;
      font-size: ${heading4FontSize};
      margin-bottom: ${spacer16};
    }
  }
`;

const Description = styled.div`
  max-width: 55%;
  @media (max-width: ${screenSmMax}) {
    padding: ${spacer16} 0;
    max-width: 100%;
  }
`;

/* TODO: this button design is too hardcoded to match the old provider directory design */
const Preferred = styled.div`
  background: ${cssColorValue('essentialAnnouncement')};
  color: ${white};
  align-self: flex-start;
  border-radius: 4px;
  padding: 0 7px 3px 7px;
`;

const OutboundLink = styled.div`
  margin: 0 ${spacer40};
  && {
    @media (max-width: ${screenSmMax}) {
      margin-left: 0;
    }
  }
`;

export const ServiceTile: React.FC<ProviderService> = (
  service: ProviderService,
) => {
  const { name, description, image, preferred, url } = service;
  const [showTooltip, setShowTooltip] = React.useState(false);
  const { width, widthRef } = useWidth();
  const trackAction = `Distributor Link - ${name} - ${url}`;
  const t = useT();

  return (
    <TileContainer ref={widthRef}>
      <Logo>
        <Link external href={url} track={[TRACK_CAT, trackAction]}>
          {image && (
            <ContentfulImage asset={image} imageStyle={{ borderRadius: 4 }} />
          )}
        </Link>
      </Logo>

      <Name>
        <div>
          {name && (
            <Link external href={url} track={[TRACK_CAT, trackAction]}>
              <h3>{name}</h3>
            </Link>
          )}
        </div>
        {preferred && (
          <Preferred>
            <TooltipTrigger
              overlay={
                showTooltip && (
                  <Tooltip>
                    {t(
                      'FRODOR_PROVIDER_DIRECTORY_SERVICE_TILE_TIP',
                      'These providers meet our highest standards for providing quality metadata and protecting against infringement.',
                      'A tool tip that shows on hover',
                    )}
                  </Tooltip>
                )
              }
              placement={
                width < 480 ? TooltipTrigger.bottom : TooltipTrigger.right
              }
              onShow={() => setShowTooltip(true)}
              onHide={() => setShowTooltip(false)}
            >
              <Type variant="finaleBold">
                {t(
                  'FRODOR_PROVIDER_DIRECTORY_PREFERRED_CTA',
                  'preferred',
                  'Button text',
                )}
              </Type>
            </TooltipTrigger>
          </Preferred>
        )}
      </Name>
      <Description>{description}</Description>
      <OutboundLink>
        <Link external href={url} track={[TRACK_CAT, trackAction]}>
          <IconWithText
            icon={() => <IconArrowRight iconSize={16} />}
            rightAlign
          >
            <Type variant="mestoBold">
              {t('FRODOR_PROVIDER_DIRECTORY_VISIT_CTA', 'Visit', 'Button text')}
            </Type>
          </IconWithText>
        </Link>
      </OutboundLink>
    </TileContainer>
  );
};
